<script setup lang="ts">
/**
 * The check itself is the uploader above this section (AtsCvChecker), so the
 * button scrolls back up to it, not to a checkout.
 *
 * ATS check page body: three service cards on the grey surface, then a content
 * block with the before/after score cards beside a dark "how it works" panel,
 * and one button. The page title band comes from MarketingFeaturePage.
 */
const { t } = useI18n()

const features = [
  { id: 'scoreAnalysis', icon: 'bi-speedometer2', points: ['compatibility', 'parsing', 'score'] },
  { id: 'keywordOptimization', icon: 'bi-key', points: ['suggestions', 'industry', 'ranking'] },
  { id: 'formatValidation', icon: 'bi-file-earmark-check', points: ['structure', 'readability', 'compatibility'] },
] as const

const issues = ['formatting', 'keywords', 'sections'] as const
const improvements = ['structure', 'keywords', 'parsing'] as const
</script>

<template>
  <section class="sofax surface-alt relative overflow-hidden">
    <div class="section-space">
      <div class="shell relative z-10">
        <SofaxSectionHeading
          :eyebrow="t('homepage.atsCheck.eyebrow')"
          :title="t('homepage.atsCheck.title')"
          :text="t('homepage.atsCheck.subtitle')"
        />

        <div class="grid grid-cols-1 gap-y-20 lg:gap-y-[6.25rem]">
          <!-- Service cards -->
          <ul class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <li
              v-for="feature in features"
              :key="feature.id"
              class="h-full rounded-[0.625rem] bg-white p-[1.875rem] transition-all duration-300 hover:shadow-lift dark:bg-gray-800"
            >
              <SofaxIconTile :icon="feature.icon" />
              <div class="display-heading display-heading-4 mb-4 mt-[1.875rem]">
                {{ t(`homepage.atsCheck.features.${feature.id}.title`) }}
              </div>
              <p class="mb-6 text-base">{{ t(`homepage.atsCheck.features.${feature.id}.description`) }}</p>
              <ul class="flex flex-col gap-3">
                <li v-for="point in feature.points" :key="point" class="flex items-start gap-3">
                  <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-xl text-accent" />
                  <span class="text-base">{{ t(`homepage.atsCheck.features.${feature.id}.points.${point}`) }}</span>
                </li>
              </ul>
            </li>
          </ul>

          <!-- Content block: score before and after, dark panel on the right -->
          <div class="grid grid-cols-1 items-center gap-10 md:gap-[3.75rem] lg:grid-cols-2 xl:grid-cols-[1fr_minmax(0,0.8fr)] xl:gap-20">
            <div>
              <div class="mb-[3.125rem]">
                <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('homepage.atsCheck.demo.title') }}</h2>
                <p class="mt-5">{{ t('homepage.atsCheck.demo.description') }}</p>
              </div>

              <div class="flex max-w-[35rem] flex-col gap-4">
                <div class="card">
                  <div class="mb-4 flex items-center justify-between gap-4">
                    <div class="display-heading display-heading-5">{{ t('homepage.atsCheck.demo.beforeScore') }}</div>
                    <span class="rounded-full bg-ink px-3 py-1 !text-sm font-bold text-white dark:bg-white dark:text-ink">42%</span>
                  </div>
                  <ul class="flex flex-col gap-2">
                    <li v-for="issue in issues" :key="issue" class="flex items-start gap-3 text-base">
                      <i class="bi bi-x-circle mt-0.5 shrink-0 text-xl text-muted" />
                      <span>{{ t(`homepage.atsCheck.demo.issues.${issue}`) }}</span>
                    </li>
                  </ul>
                </div>

                <div class="flex justify-center" aria-hidden="true">
                  <SofaxIconTile icon="bi-arrow-down" round size="sm" />
                </div>

                <div class="card border-accent bg-accent-soft/20 dark:bg-accent/10">
                  <div class="mb-4 flex items-center justify-between gap-4">
                    <div class="display-heading display-heading-5">{{ t('homepage.atsCheck.demo.afterScore') }}</div>
                    <span class="rounded-full bg-accent px-3 py-1 !text-sm font-bold text-white">89%</span>
                  </div>
                  <ul class="flex flex-col gap-2">
                    <li v-for="improvement in improvements" :key="improvement" class="flex items-start gap-3 text-base">
                      <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-xl text-accent" />
                      <span>{{ t(`homepage.atsCheck.demo.improvements.${improvement}`) }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="relative mx-auto w-full max-w-[31.25rem]">
              <div class="rounded-[0.625rem] bg-ink p-[1.875rem] text-white dark:bg-gray-800 md:p-10">
                <SofaxIconTile icon="bi-shield-check" round size="md" />
                <div class="display-heading display-heading-3 mb-3 mt-8 !text-white">{{ t('homepage.atsCheck.demo.powered') }}</div>
                <p class="!text-white/75">{{ t('homepage.atsCheck.demo.technology') }}</p>
                <div class="mt-6 rounded-[0.625rem] bg-white/10 p-4 text-base font-semibold">
                  <i class="bi bi-lightning-charge-fill mr-2 text-accent-soft" />{{ t('homepage.atsCheck.demo.instantResults') }}
                </div>
                <p class="mt-6 text-sm !text-white/60">{{ t('homepage.atsCheck.demo.disclaimer') }}</p>
              </div>
              <img
                src="/sofax/content-shape-1.svg"
                alt=""
                aria-hidden="true"
                width="125"
                height="137"
                class="pointer-events-none absolute -right-10 -top-10 -z-10 hidden xl:inline-block"
              >
            </div>
          </div>

          <div class="text-center">
            <SofaxBtn
              to="#check"
              :label="t('homepage.atsCheck.cta')"
              variant="accent"
            />
            <p class="mt-4 text-sm text-muted dark:text-gray-400">{{ t('homepage.atsCheck.freeNote') }}</p>
          </div>
        </div>
      </div>

      <img
        src="/sofax/background-shape-1.svg"
        alt=""
        aria-hidden="true"
        width="174"
        height="171"
        class="pointer-events-none absolute -left-10 bottom-32 -z-10 hidden max-w-[153px] opacity-70 xl:inline-block"
      >
    </div>
  </section>
</template>
